import { getUserDetails } from '@api/user-api';
import { Banner25 } from '@components/Banner25/Banner25';
import { CallbackErrorBoundary } from '@components/Error/CallbackErrorBoundary';
import EmailVerify from '@components/oauth/EmailVerify';
import { useCelloReferralCompletion } from '@hooks/cello/use-cello-referral-completion';
import { useCreateUserDoc } from '@hooks/use-create-user-doc';
import useMedia from '@hooks/use-media';
import useNoRenderRef from '@hooks/use-no-render-ref';
import useNode from '@hooks/use-node';
import useOnFocus from '@hooks/use-on-focus';
import { useTimer } from '@hooks/use-timer';
import useValidateInput, { emailValidator, passwordValidators } from '@hooks/use-validate-input';
import { PinInput } from '@mantine/core';
import { analytics } from '@modules/analytics';
import {
  AuthConstants,
  LoginMethod,
  type LoginMethodPayload,
  type OAuthProvider,
  OtpAuthenticateError,
  OtpLoginOrCreateError,
  StytchPasswordError,
  type UserInfo,
  addEmailAndPasswordLoginToCurrentUser,
  addSocialProviderLoginToCurrentUser,
  authEvents,
  getProviderMismatchMessage,
  getStytchLoginErrorMessage,
  isRateLimitError,
  loginOrCreateErrors,
  mapLoginMethodToOAuthProvider,
  pickLoginMethodForEmail,
  sendResetLink,
  useAuthenticateStytchPassword,
  useOtpAuthenticate,
  useOtpLoginOrCreate,
  useSetLoginPreference,
  useSetStytchPassword,
} from '@modules/auth-helpers';
import {
  CELLO_REFERRAL_FLAG,
  CELLO_REFERRAL_PAYWALL_ROUTE,
  getCachedCelloReferralCode,
  waitForCelloReferralVariant,
} from '@modules/cello-referral';
import { useFeatureFlag } from '@modules/feature-flag';
import { type FirebaseUser, auth } from '@modules/firebase';
import { authService } from '@modules/firebase-auth/service';
import { GoogleTagManagerUtils } from '@modules/google-tag-manager/google-tag-manager-utils';
import { createChildLogger } from '@modules/logger';
import { createNewUser } from '@modules/user-helpers';
import { getUserHomePageRoute } from '@modules/user-profile/user-profile-helpers';
import { isEmail } from '@modules/validators';
import { Button, IconCloseCircle, IconEyeHide, IconEyeShow, Loader, TextField } from '@ui-lib';
import { Toast, type ToastRef } from '@ui-lib/components/Toast/Toast';
import type { AuthError } from 'firebase/auth';
import IsPwned from 'is-pwned';
import { AnimatePresence, motion } from 'motion/react';
import { nanoid } from 'nanoid';
import Router from 'next/router';
import {
  type FormEvent,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { SSOLoginButton } from './components/SSOLoginButton';
import { SwitchToWorkEmailPrompt } from './components/SwitchToWorkEmailPrompt';
import styles from './LoginForm.module.scss';
import { type LoginFormProps, LoginState } from './types';

const logger = createChildLogger({ module: 'components/LoginForm' });

// The sign-up OTP (auth-service otps.email.loginOrCreate) expires after 10 minutes. Within that window
// the enrolment verify step reuses the already-emailed code instead of sending a second one.
const SIGNUP_OTP_REUSE_WINDOW_MS = 10 * 60 * 1000;

export interface LoginFormRef {
  goBack: () => void;
}

export const LoginForm = ({
  ref,
  beforeLoginHook,
  email,
  onEmailChange,
  password,
  onPasswordChange,
  mode,
  onShowLogin,
  onShowReset,
  onLogin,
  onLink,
  newUserDetails = {},
  enableClearEmailButton = false,
  lockEmail = false,
  reuseAnonUserIfPossible = false,
  preferWorkEmail = false,
  authProfile,
  setAuthProfile,
  enteredPersonalEmail,
  setEnteredPersonalEmail,
  favouredEmail,
  setFavouredEmail,
  setLoginState,
  refPage,
  usePopupAuth,
}: LoginFormProps & {
  ref?: React.Ref<LoginFormRef>;
}) => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const [userLoginMethodOverride, setUserLoginMethodOverride] = useState<LoginMethod | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [mainButtonLoading, setMainButtonLoading] = useState(false);
  const [isContinuingWithCode, setIsContinuingWithCode] = useState(false);
  // Set when the OAuth provider rejected sign-in with auth/provider-already-linked: the account's provider is
  // bound to a different identity (e.g. a recreated Google Workspace account), so offer the email code instead.
  const [isProviderMismatch, setIsProviderMismatch] = useState(false);
  const [isResendingCode, setIsResendingCode] = useState(false);
  const [hasChosenPassword, setHasChosenPassword] = useState(false);
  const [hasChosenOtp, setHasChosenOtp] = useState(false);
  const [resendCodeSeconds, setResendCodeSeconds] = useTimer();
  const [emailLoginInputNode, emailLoginInputRef] = useNode();
  const [passwordInputNode, passwordInputRef] = useNode();
  const [passwordErrorMessage, setPasswordErrorMessage] = useState('');
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isPasswordError, newPasswordErrorMessage] = useValidateInput(password, passwordValidators);
  const [, , emailLoginInputBlurred] = useOnFocus(emailLoginInputNode);
  const [isLoginEmailError, loginEmailErrorMessage] = useValidateInput(email, [emailValidator]);
  const [loginEmailFirebaseError, setLoginEmailFirebaseError] = useState('');
  const [resetEmailInputNode, resetEmailInputRef] = useNode();
  const [, , resetEmailInputBlurred] = useOnFocus(resetEmailInputNode);
  const [, passwordHasFocussed, passwordHasBlurred] = useOnFocus(passwordInputNode, true);
  const [isResetEmailError, resetEmailErrorMessage] = useValidateInput(email, [emailValidator]);
  const [sentResetEmail, setSentResetEmail] = useState(false);
  const [generatingEmailReset, setGeneratingEmailReset] = useState(false);
  const [fetchingLoginMethods, setFetchingAuthProfile] = useState<undefined | 'auto' | 'manual'>(
    undefined,
  );
  const loginModeRef = useRef<'login' | 'signup'>('login');

  // Stytch password enrolment gate. Password sign-up / lazy migration must enrol the password into Stytch
  // before the sign-in navigation unmounts this form (the Firebase sign-in fires onIdTokenChanged, whose
  // navigation would otherwise cancel the in-flight enrolment). While the gate is set, onLoginEvent parks
  // its userInfo instead of navigating; completeAfterEnrolment() resumes it once enrolment resolves.
  const stytchEnrolGateRef = useRef(false);
  const pendingLoginUserInfoRef = useRef<UserInfo | null>(null);
  // When the Stytch user's email needs verifying before its password can be seeded, this holds the pending
  // enrolment while the one-time email-OTP step (EmailVerify) runs. emailId carries the method id of a
  // recently-sent sign-up OTP so the step can reuse that code instead of emailing a second one.
  const [stytchVerifyState, setStytchVerifyState] = useState<{
    email: string;
    password: string;
    emailId?: string;
  } | null>(null);

  // email otp state
  const requestId = useRef<string>(nanoid());
  const [otpLoginOrCreate] = useOtpLoginOrCreate(requestId.current);
  const [otpAuthenticate] = useOtpAuthenticate(requestId.current);
  const [authenticateStytchPassword] = useAuthenticateStytchPassword();
  const [setStytchPassword] = useSetStytchPassword();
  const [otpMethodId, setOtpMethodId] = useState<string | null>(null);
  // When the sign-up OTP was emailed — bounds how long its code can be reused by the verify step.
  const otpSentAtRef = useRef<number | null>(null);
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState('');
  const pinInputRef = useRef<HTMLInputElement>(null);
  const loginSentToastRef = useRef<ToastRef>(null);
  const [isCatastrophe, setIsCatastrophe] = useState(false);

  const goToCatastropheFlow = useCallback(() => {
    setIsCatastrophe(true);
    setHasChosenPassword(false);
    setHasChosenOtp(false);
    setOtpMethodId(null);
    setLoginState?.(LoginState.CATASTROPHIC_ERROR);
  }, [setLoginState]);

  const goToPassword = useCallback(() => {
    setHasChosenPassword(true);
    setUserLoginMethodOverride(LoginMethod.PASSWORD);
    setHasChosenOtp(false);
    setIsCatastrophe(false);
    setLoginState?.(
      mode === 'link'
        ? LoginState.FINISH_SIGNING_UP_PASSWORD
        : LoginState.FINISH_SIGNING_IN_PASSWORD,
    );
  }, [setLoginState, mode]);

  async function goToOtp() {
    let isSuccess = true;
    if (!otpMethodId) {
      isSuccess = await sendOtpCode();
    }
    if (isSuccess) {
      setHasChosenOtp(true);
      setHasChosenPassword(false);
      setIsCatastrophe(false);
      setUserLoginMethodOverride(LoginMethod.EMAIL_OTP);
      setLoginState?.(
        mode === 'link' ? LoginState.FINISH_SIGNING_UP_OTP : LoginState.FINISH_SIGNING_IN_OTP,
      );
    }
    return isSuccess;
  }

  const sendOtpCode = useCallback(async () => {
    let methodId: string | undefined;

    const MESSAGE = 'OTP login or create operation completed.';
    try {
      analytics.track({
        name: 'otp_request_sent',
        properties: {
          type: authProfile.type,
          email,
          request_id: requestId.current,
          domain: email.split('@')[1],
        },
      });
      methodId = await otpLoginOrCreate(email);
      setOtpMethodId(methodId);
      otpSentAtRef.current = Date.now();
      setHasChosenOtp(true);
      setLoginState?.(
        mode === 'link' ? LoginState.FINISH_SIGNING_UP_OTP : LoginState.FINISH_SIGNING_IN_OTP,
      );
      setResendCodeSeconds(59);
      analytics.track({
        name: 'otp_verification_viewed',
        properties: {
          type: authProfile.type,
          domain: email.split('@')[1],
        },
      });
      logger.info(MESSAGE, { email, methodId });
      return !!methodId;
    } catch (error) {
      // fallback to using password if it is a valid login option
      logger.error(MESSAGE, { error, email });

      const isBigError = error instanceof OtpLoginOrCreateError && error.code === 'unknown_error';

      if (hasChosenPassword || methodId) {
        const fallback =
          'There was an error when trying to send an email code. Please try again later.';
        if (error instanceof OtpLoginOrCreateError) {
          const message = loginOrCreateErrors.get(error.code) || fallback;
          setErrorMessage(message);
        } else {
          setErrorMessage(fallback);
        }
        return false;
      }

      if (authProfile.enabledMethods?.includes(LoginMethod.PASSWORD)) {
        logger.info('OTP login or create operation failed. Falling back to password.', {
          error,
          email,
          enabledMethods: authProfile.enabledMethods,
        });
        goToPassword();
        return false;
      } else {
        if (isBigError) {
          logger.warn('Catastrophic error when trying to send OTP code.', {
            email,
            error,
            enabledMethod: authProfile.enabledMethods,
          });
          analytics.track({
            name: 'otp_failed_viewed',
            properties: {
              type: authProfile.type,
              request_id: requestId.current,
            },
          });
        } else {
          logger.error('Unknown error when trying to send OTP code.', { error });
        }

        goToCatastropheFlow();
        return false;
      }
    }
  }, [
    otpLoginOrCreate,
    setLoginState,
    email,
    authProfile,
    hasChosenPassword,
    mode,
    setResendCodeSeconds,
    goToCatastropheFlow,
    goToPassword,
  ]);

  function goBack() {
    // basically clears all relevant state to initial state except email
    setHasChosenPassword(false);
    setHasChosenOtp(false);
    setUserLoginMethodOverride(null);
    onShowLogin();
    setPasswordErrorMessage('');
    onPasswordChange('');
    setIsPasswordVisible(false);
    setOtpCode('');
    setOtpError('');
    setLoginState?.(
      mode === 'link' ? LoginState.CREATE_A_BLINQ_ACCOUNT : LoginState.SIGN_IN_OR_SIGN_UP,
    );
    setIsCatastrophe(false);
  }

  useImperativeHandle(ref, () => ({ goBack }));

  const handleOtpLogin = useCallback(async () => {
    setMainButtonLoading(true);
    if (!otpMethodId) {
      return;
    }
    const start = performance.now();
    const MESSAGE = 'OTP authenticate operation completed.';
    const log = logger.child({
      email,
      methodId: otpMethodId,
      mode,
      is_existing_user: authProfile.isExistingUser,
      has_password: authProfile.enabledMethods?.includes(LoginMethod.PASSWORD),
    });
    try {
      analytics.track({
        name: 'otp_verification_sent',
        properties: {
          type: authProfile.type,
          domain: email.split('@')[1],
          email,
          request_id: requestId.current,
        },
      });

      const token = await otpAuthenticate(otpMethodId, otpCode);

      log.info(MESSAGE);

      const end = performance.now();
      const duration = end - start;

      analytics.track({
        name: 'otp_verification_received',
        properties: {
          type: authProfile.type,
          domain: email.split('@')[1],
          email,
          request_id: requestId.current,
          result: 'success',
          duration,
        },
      });
      await authService.signInWithCustomToken(token);

      log.info('Signed in with custom token from otp using firebase');

      setSubmitted(true);
    } catch (error) {
      const errorLog = log.child({ error });

      const end = performance.now();
      const duration = end - start;

      if (error instanceof OtpAuthenticateError) {
        analytics.track({
          name: 'otp_verification_received',
          properties: {
            type: authProfile.type,
            domain: email.split('@')[1],
            email,
            request_id: requestId.current,
            result: 'failed',
            duration,
            error_code: error.code,
          },
        });
      }

      if (error instanceof OtpAuthenticateError && error.code === 'invalid_code') {
        errorLog.info('User entered invalid OTP code.');

        setOtpError('The code you entered is incorrect. Please try again.');
      } else if (error instanceof OtpAuthenticateError && error.code === 'expired_code') {
        errorLog.info('Expired OTP entered by user.');

        await sendOtpCode();

        setOtpError("The code is no longer valid. We've sent a new code to your email.");
      } else if (error instanceof OtpAuthenticateError && error.code === 'access_restricted') {
        errorLog.info('User tried to authenticate with a restricted stytch account');

        if (authProfile.enabledMethods?.includes(LoginMethod.PASSWORD)) {
          setErrorMessage(
            authProfile.isExistingUser
              ? 'Oops, that was one too many failed attempts. Try signing in with password instead.'
              : 'Oops, that was one too many failed attempts. Try signing up with a password instead.',
          );
          goToPassword();
          authProfile.enabledMethods = authProfile.enabledMethods?.filter(
            (it) => it !== LoginMethod.EMAIL_OTP,
          );
        } else {
          goToCatastropheFlow();
        }
      } else if (error instanceof OtpAuthenticateError && error.code === 'unknown_error') {
        errorLog.error('Error when trying to authenticate OTP. Catastrophe flow initiated.');

        if (authProfile.enabledMethods?.includes(LoginMethod.PASSWORD)) {
          setErrorMessage(
            authProfile.isExistingUser
              ? 'Oops, something went wrong. Try signing in with password instead.'
              : 'Oops, something went wrong. Try signing up with a password instead.',
          );
          goToPassword();
          authProfile.enabledMethods = authProfile.enabledMethods?.filter(
            (it) => it !== LoginMethod.EMAIL_OTP,
          );
        } else {
          goToCatastropheFlow();
        }
        analytics.track({
          name: 'otp_failed_viewed',
          properties: {
            type: authProfile.type,
            request_id: requestId.current,
          },
        });
      } else {
        errorLog.error(MESSAGE);
      }

      setMainButtonLoading(false);
    }
  }, [
    otpMethodId,
    otpCode,
    otpAuthenticate,
    sendOtpCode,
    email,
    authProfile,
    goToCatastropheFlow,
    goToPassword,
    mode,
  ]);

  // log in automatically when the user has entered six digits into the input field
  const previousOtpCodeLength = useRef(0);
  useEffect(() => {
    if (previousOtpCodeLength.current !== 6 && otpCode.length === 6 && !mainButtonLoading) {
      void handleOtpLogin();
    }
    previousOtpCodeLength.current = otpCode.length;
  }, [otpCode, handleOtpLogin, mainButtonLoading]);

  const { createUserDoc } = useCreateUserDoc();
  const [userDocCreated, setUserDocCreated] = useState(false);

  const newUserDetailsRef = useNoRenderRef(newUserDetails);
  // Through a ref: the variant resolves asynchronously and the login handler
  // below is memoised, so capturing it by closure would freeze it at 'off'.
  const [celloReferralVariant, { loading: celloReferralFlagLoading }] = useFeatureFlag(
    CELLO_REFERRAL_FLAG,
    'off',
    false,
  );
  const celloReferralVariantRef = useNoRenderRef(celloReferralVariant);
  const celloReferralFlagLoadingRef = useNoRenderRef(celloReferralFlagLoading);
  const { reportReferredSignup } = useCelloReferralCompletion();

  const resetPasswordMode = mode === 'reset';

  const screenSizeCategory = useMedia<'small' | 'large'>(
    [`(min-width: ${701}px)`],
    ['large'],
    'small',
  );

  // @skooch
  //
  // This should be merged with [isPasswordError, newPasswordErrorMessage] as that uses
  // @hooks/use-validate-input - but for now we'll keep it separate due to complexity
  const pwValidatorsPassed = password.length > 9 && /\d/.test(password) && /[A-Z]/.test(password);

  // Captures LS_SSO_METHOD at mount, gated on LS_SSO_LOGIN=1 (which indicates we're
  // returning from an active OAuth redirect). Reading LS_SSO_METHOD from localStorage
  // directly inside onLoginEvent is unsafe — the value persists across aborted SSO
  // attempts and poisons subsequent non-SSO sign-ins via setLoginPreference.
  const ssoRedirectMethodRef = useRef<string | null>(null);
  const ssoRedirectEmailRef = useRef<string | null>(null);

  useEffect(() => {
    if (localStorage.getItem(AuthConstants.LS_SSO_LOGIN)) {
      ssoRedirectMethodRef.current = localStorage.getItem(AuthConstants.LS_SSO_METHOD);
      ssoRedirectEmailRef.current = sessionStorage.getItem(AuthConstants.SS_SSO_EMAIL);
      setLoading(true);
      setSubmitted(true);
      localStorage.removeItem(AuthConstants.LS_SSO_LOGIN);
      localStorage.removeItem(AuthConstants.LS_SSO_METHOD);
    }
    sessionStorage.removeItem(AuthConstants.SS_SSO_EMAIL);
  }, []);

  const [setLoginPreference] = useSetLoginPreference();

  const getLoginPreference = useCallback(
    (method?: LoginMethodPayload) => {
      const method_ = method || authProfile;
      return userLoginMethodOverride || method_.loginPreference || method_.methodId;
    },
    [authProfile, userLoginMethodOverride],
  );

  const onLoginEvent = useCallback(
    async (userInfo: UserInfo | null) => {
      // determine login method used
      let loginMethodUsed: string | LoginMethod | null;
      const ssoMethod = ssoRedirectMethodRef.current;
      if (hasChosenOtp) {
        loginMethodUsed = LoginMethod.EMAIL_OTP;
      } else if (hasChosenPassword) {
        loginMethodUsed = LoginMethod.PASSWORD;
      } else if (ssoMethod) {
        loginMethodUsed = ssoMethod;
      } else {
        loginMethodUsed = null;
      }

      const log = logger.child({
        email,
        mode: getLoginPreference(),
        login_method_used: loginMethodUsed,
        // Bind identity fields, never the FirebaseUser itself: it serialises its
        // stsTokenManager, which would put access and refresh tokens into Datadog.
        user_id: userInfo?.user.uid ?? null,
        is_anon_user: userInfo?.isAnonUser ?? null,
        is_new_user: userInfo?.isNewUser ?? null,
      });

      if (loginMethodUsed === null) {
        log.warn('Could not determine login method used.');
      }

      // Ignore if user has not performed any action
      if (!submitted) {
        log.warn('Login event received before submission. Ignoring.');
        return;
      }

      // Non-SSO completion: drop any LS_SSO_METHOD that may have leaked from an
      // earlier aborted SSO attempt, so it can't affect future sign-ins.
      if (loginMethodUsed === LoginMethod.EMAIL_OTP || loginMethodUsed === LoginMethod.PASSWORD) {
        localStorage.removeItem(AuthConstants.LS_SSO_METHOD);
        localStorage.removeItem(AuthConstants.LS_SSO_LOGIN);
      }

      if (!userInfo) {
        // sometimes, this observer gets triggered with no user, which comes from the upstream call to firebase auths
        // onIdTokenChanged. its not clear why this happens, and there doesn't seem to be much we can do about it
        // other than surface an error to the user.
        log.warn('Login event received with no user info. Ignoring.');
        setErrorMessage(
          mode === 'link' || (mode === 'hybrid' && !authProfile.isExistingUser)
            ? 'An error occurred while signing up'
            : 'An error occurred while signing in',
        );
        setLoading(false);
        return;
      }

      // A Stytch password enrolment is in flight for this sign-in. Navigating now unmounts the form and
      // cancels the in-flight setStytchPassword, so park the event; completeAfterEnrolment() re-runs it
      // (with the gate cleared) once enrolment — or the email-verify step it may route through — finishes.
      if (stytchEnrolGateRef.current) {
        pendingLoginUserInfoRef.current = userInfo;
        log.info('Holding login completion until Stytch password enrolment resolves.');
        return;
      }

      const firebaseUser: FirebaseUser = userInfo.user;

      // Fetch legacy user details for the user, for now
      const detailsStart = performance.now();
      let legacyUserDetails: Awaited<ReturnType<typeof getUserDetails>>;
      try {
        [legacyUserDetails] = await Promise.all([
          getUserDetails(firebaseUser.uid),
          loginMethodUsed ? setLoginPreference(loginMethodUsed) : async () => undefined,
        ]);
      } catch (error) {
        // Nothing below this point runs if either call rejects, so make the stall visible.
        log.error('Failed to resolve user details and login preference.', {
          duration_ms: Math.round(performance.now() - detailsStart),
          error,
        });
        throw error;
      }

      log.info('Resolved user details and login preference.', {
        duration_ms: Math.round(performance.now() - detailsStart),
        has_legacy_user: !!legacyUserDetails?.user,
      });

      // Check if a user has created a new Firebase account via the login form. If so create a
      // corresponding Blinq user for them.
      if (!legacyUserDetails?.user && mode === 'hybrid' && firebaseUser && !userDocCreated) {
        log.info('User created a new Firebase account via the login form. Creating new user doc.');
        const newUser = createNewUser(firebaseUser, newUserDetailsRef.current);

        await createUserDoc(newUser);
        setUserDocCreated(true);

        log.info('User created a new Firebase account via the login form. Created new user doc.');
        onLogin?.({ ...legacyUserDetails, user: newUser }, 'signup');

        return;
      }

      // set the sign_up_type trait in amplitude for analytics
      if (!authProfile.isExistingUser) {
        analytics.identify({
          traits: {
            sign_up_type: hasChosenOtp ? 'otp' : hasChosenPassword ? 'password' : 'sso',
          },
          user_id: userInfo.user.uid,
        });
      }

      try {
        if (onLogin) {
          // `loginModeRef` is only flipped to 'signup' by the password submit path. Accounts
          // created server-side (OTP's `otpAuthenticate`, SSO) reach here with the ref still
          // 'login', which made every referred OTP signup silently skip the Cello report
          // (ACT-2096). The auth profile already knows whether this account is new, so
          // dispatch on that; the API re-checks account age before attributing.
          const dispatchMode = authProfile.isExistingUser ? loginModeRef.current : 'signup';
          log.info('Dispatching auth completion to onLogin', { dispatch_mode: dispatchMode });
          onLogin(legacyUserDetails, dispatchMode);
          return;
        }

        if (onLink) {
          log.info('User linked. Dispatching to onLink.');
          onLink();
          return;
        }
      } catch (error) {
        log.error('Error dispatching login event to onLogin or onLink.', { error });
        setErrorMessage('An error occurred while logging in');
        setLoading(false);
        return;
      }

      if (userInfo.isAnonUser) {
        log.error('onLoginEvent triggered with an anonymous user.');
        setErrorMessage('An error occurred while logging in');
        setLoading(false);
        return;
      }

      // isNewUser is server-derived at session start; a client Firestore read here races the upsert
      if (mode === 'login' && userInfo.isNewUser) {
        // The referral landing page's "Claim" button points here, not at
        // /onboarding/signup, so this — not SignupStep — is where most referred
        // accounts are created, SSO ones included. Attribution is reported now rather
        // than at the end of onboarding: a user who abandons half way still earned
        // their referrer the signup, and the API's account-age window is finite
        // (ACT-2224). The discount itself is offered at the customize-card exit, which
        // is where this detour finishes.
        //
        // Guarded because everything below is the navigation off /login: an unhandled
        // rejection here skips it and leaves the spinner up forever, which is the
        // symptom of BIZ-2831. A dropped attribution is the acceptable trade (BIZ-2876).
        try {
          log.info('Reporting the referred signup before onboarding');
          await reportReferredSignup(userInfo.user.uid);
        } catch (error) {
          log.error('Failed to report the referred signup, continuing to onboarding', { error });
        }

        const signUpLink =
          screenSizeCategory === 'small'
            ? '/onboarding/personal-details?source=onboarding'
            : '/onboarding/customize-card?source=onboarding';

        void Router.push(signUpLink);
        return;
      }

      // A referred user who already had an account reaches the offer here rather
      // than on the dashboard: below the tablet breakpoint the dashboard is
      // replaced wholesale by the "download the app" overlay, so this is the only
      // point in a mobile session where the discount can be shown. Once they've
      // bought or declined, the paywall forwards to the post-signup destination
      // for their viewport: home on desktop, the "Get the app" step on mobile.
      //
      // Guarded for the same reason as the report above: a failure in here must fall
      // through to the default destination rather than strand the user on /login
      // (BIZ-2876).
      try {
        if (getCachedCelloReferralCode()) {
          // The provider refetches variants when the Firebase identity changes, so
          // immediately after sign-in the value here can still be the one resolved
          // for the signed-out device. Reading it too early drops the offer for a
          // user who is bucketed in. Only referred users wait, and only briefly —
          // everyone else's login is untouched.
          log.info('Waiting on the Cello referral variant before routing');
          await waitForCelloReferralVariant(celloReferralFlagLoadingRef);

          if (celloReferralVariantRef.current === 'on') {
            log.info('Routing the referred user to the Cello paywall');
            void Router.push(`${CELLO_REFERRAL_PAYWALL_ROUTE}?returning=1`);
            return;
          }

          log.info('Referred user is not in the Cello variant, using the default destination');
        }
      } catch (error) {
        log.error('Cello referral routing failed, using the default destination', { error });
      }

      // Individual workspace owners go to "/" for server-side feature flag
      // routing to /dash (when enabled) or /cards/personal (default).
      // All other users go directly to their home page.
      const isIndividualOwner =
        userInfo.userDetails.currentWorkspace.type === 'INDIVIDUAL' &&
        userInfo.userDetails.currentWorkspace.profile.role === 'OWNER';
      if (isIndividualOwner) {
        void Router.push('/');
      } else {
        void Router.push(getUserHomePageRoute(userInfo.userDetails));
      }
    },
    [
      submitted,
      mode,
      newUserDetailsRef,
      celloReferralVariantRef,
      celloReferralFlagLoadingRef,
      reportReferredSignup,
      onLogin,
      onLink,
      screenSizeCategory,
      createUserDoc,
      userDocCreated,
      setLoginPreference,
      getLoginPreference,
      hasChosenPassword,
      hasChosenOtp,
      email,
      authProfile,
    ],
  );

  // Listen for login events that could be the result of a successful social sign in / email link sign in & dispatch to onLogin or onLink
  useEffect(() => {
    const unsubscribe = authEvents.onLogin(onLoginEvent);

    return () => {
      unsubscribe();
    };
  }, [onLoginEvent]);

  // Clears the enrolment gate and resumes the login-completion that onLoginEvent parked while enrolment
  // was in flight (see the gate check in onLoginEvent). Deliberately resumes through this render's
  // onLoginEvent closure — the one live when the submit began — so the doc-create fallback still applies
  // if the user-details read lags the just-written doc. No-op if the sign-in event hasn't arrived yet — a
  // later onLoginEvent then completes normally with the gate already cleared.
  function completeAfterEnrolment() {
    stytchEnrolGateRef.current = false;
    const parked = pendingLoginUserInfoRef.current;
    pendingLoginUserInfoRef.current = null;
    if (parked) {
      onLoginEvent(parked).catch((error) => {
        logger.error('Failed to complete login after Stytch password enrolment.', { email, error });
        setErrorMessage('An error occurred while logging in');
        setMainButtonLoading(false);
      });
    }
  }

  // Seeding dual-writes the password into Firebase, which revokes the session's refresh token — a
  // token refresh fails with TOKEN_EXPIRED and the SDK signs the user out (observed as landing back on
  // /login after sign-up). The session is unrecoverable; re-authenticate with the just-set credentials
  // to mint a fresh one before resuming completion. Never throws; on failure the old session's normal
  // failure paths apply.
  async function reauthenticateAfterEnrolment(emailUsed: string, passwordUsed: string) {
    try {
      await authService.signInWithEmailAndPassword(emailUsed, passwordUsed);
      logger.info('Re-authenticated after Stytch password enrolment.', { email: emailUsed });
    } catch (error) {
      logger.warn('Failed to re-authenticate after Stytch password enrolment.', {
        email: emailUsed,
        error,
      });
    }
  }

  // Enrols the current password into Stytch, then resumes login completion. Enrolment is gated server-side
  // by the stytch-password flag (with the authenticated identity) and dual-writes Firebase; a failure is
  // non-fatal and retries on next sign-in. When the Stytch user's email must be verified first, this routes
  // into the one-time email-OTP step (EmailVerify) and completion resumes from onStytchEmailVerified.
  async function enrolStytchPasswordAndComplete() {
    const log = logger.child({ email });
    try {
      await setStytchPassword(password);
      await reauthenticateAfterEnrolment(email, password);
    } catch (error) {
      if (error instanceof StytchPasswordError && error.code === 'email_verification_required') {
        log.info(
          'Stytch password enrolment needs email verification; routing to the email-OTP step.',
        );
        // Reuse the sign-up OTP already in the user's inbox when it can still be valid.
        const reusableEmailId =
          otpMethodId &&
          otpSentAtRef.current !== null &&
          Date.now() - otpSentAtRef.current < SIGNUP_OTP_REUSE_WINDOW_MS
            ? otpMethodId
            : undefined;
        setStytchVerifyState({ email, password, emailId: reusableEmailId });
        return;
      }
      log.warn('Best-effort Stytch password enrolment failed; will retry next sign-in.', { error });
    }
    completeAfterEnrolment();
  }

  // Called after the email-OTP step verifies the Stytch email. The verified email now unblocks the seed, so
  // retry once; any residual failure is non-fatal (retries next sign-in) and must not strand the user on the
  // verify screen.
  async function onStytchEmailVerified() {
    const pending = stytchVerifyState;
    if (!pending) {
      return;
    }
    const log = logger.child({ email: pending.email });
    try {
      // Retry with the password stashed when enrolment was deferred, not the live form state.
      await setStytchPassword(pending.password);
      log.info('Stytch password enrolled after email verification.');
      await reauthenticateAfterEnrolment(pending.email, pending.password);
    } catch (error) {
      log.warn(
        'Stytch password enrolment after email verification failed; will retry next sign-in.',
        {
          error,
        },
      );
    }
    setStytchVerifyState(null);
    completeAfterEnrolment();
  }

  async function signUserUpWithPassword() {
    const log = logger.child({ email });
    const queryParams = new URLSearchParams(window.location.search);
    if (!password) {
      setPasswordErrorMessage('Please enter a password');
      setMainButtonLoading(false);
      return;
    }
    if (isPasswordError) {
      setPasswordErrorMessage(newPasswordErrorMessage);
      setMainButtonLoading(false);
      return;
    }
    loginModeRef.current = 'signup';

    // Set the gate before the Firebase sign-up: createUserWithEmailAndPassword fires onIdTokenChanged
    // synchronously, and an ungated navigation would unmount this form and cancel the enrolment below.
    stytchEnrolGateRef.current = true;
    try {
      if (reuseAnonUserIfPossible && auth.currentUser) {
        await addEmailAndPasswordLoginToCurrentUser(email, password);
        log.info('Used anonymous user to sign user up with password.');
      } else {
        const result = await authService.createUserWithEmailAndPassword(email, password);

        log.info('User signed up with password.');

        if (result.user) {
          // Include the parent's newUserDetails (e.g. names on the claim pages): the doc-create fallback
          // in onLoginEvent that used to write them rarely runs now that this write completes before the
          // parked login event resumes.
          const details = newUserDetailsRef.current;
          const newUser = createNewUser(result.user, {
            ...details,
            signInEmailAddress: email,
            firstName: details.firstName || queryParams.get('first_name') || undefined,
            lastName: details.lastName || queryParams.get('last_name') || undefined,
          });

          await createUserDoc(newUser);
          log.info('User doc created for user sign up with password.', { userDoc: newUser });
        }

        setUserDocCreated(true);
      }
    } catch (error) {
      // Firebase sign-up failed: release the gate so a retry isn't wedged, and let handleLogin surface it.
      stytchEnrolGateRef.current = false;
      throw error;
    }

    await enrolStytchPasswordAndComplete();
  }

  async function proceedToDefaultMethod() {
    switch (authProfile.defaultMethod) {
      case LoginMethod.EMAIL_OTP:
        await goToOtp();
        logger.info('Directed new user to OTP sign in based on defaultMethod.', {
          email,
          authProfile,
        });
        break;
      default:
        goToPassword();
        logger.info('Directed new user to password sign in based on defaultMethod.', {
          email,
          authProfile,
        });
        break;
    }
  }

  async function handleLogin(method: LoginMethodPayload) {
    const log = logger.child({ email, mode });
    const signInOAuth = usePopupAuth
      ? authService.signInWithPopup.bind(authService)
      : authService.signInWithRedirect.bind(authService);

    try {
      setMainButtonLoading(true);

      log.info('handleLogin', { method, mode });

      // If SSO enforced, ignore everything else and go straight to SSO
      if (method.methodId && method.ssoEnforced) {
        logger.info('handleLogin: SSO enforced', { method });
        if (!method.defaultMethod) {
          throw new Error('sso enforced but no defaultMethod provided. this should never happen.');
        }
        const oauthLoginProvider = mapLoginMethodToOAuthProvider(method.defaultMethod);
        await signInOAuth({ email, providerId: oauthLoginProvider });
        setMainButtonLoading(false);
        return;
      }

      if (!authProfile.isExistingUser && hasChosenPassword) {
        // sign them up with password
        await signUserUpWithPassword();
        return;
      }
      switch (getLoginPreference(method) || authProfile.methodId) {
        case LoginMethod.PASSWORD:
          if (!hasChosenPassword) {
            goToPassword();
            setMainButtonLoading(false);
            return;
          }
          if (!passwordHasFocussed) {
            setSubmitted(false);
          }
          if (!password) {
            setMainButtonLoading(false);
            setPasswordErrorMessage('Please enter a password');
            return;
          }
          if (authProfile.passwordProvider === 'stytch') {
            // Password lives in Stytch: authenticate there, then sign into Firebase with the custom token.
            try {
              const customToken = await authenticateStytchPassword(email, password);
              await authService.signInWithCustomToken(customToken);
              log.info('Signed in with Stytch password.');
            } catch (stytchError) {
              // Firebase can hold a newer password than Stytch if the user reset it out-of-band (Firebase
              // forgot-password). Fall back to the Firebase password before failing so a reset doesn't lock
              // the user out; a genuinely wrong password fails here too and surfaces as "Wrong password".
              if (
                stytchError instanceof StytchPasswordError &&
                stytchError.code === 'unauthorized_credentials'
              ) {
                log.info('Stytch password rejected; falling back to the Firebase password.');
                await authService.signInWithEmailAndPassword(email, password);
              } else {
                throw stytchError;
              }
            }
          } else {
            // Set the gate before signing in: signInWithEmailAndPassword wakes onIdTokenChanged, whose
            // navigation would otherwise cancel the lazy Stytch migration below.
            stytchEnrolGateRef.current = true;
            try {
              await authService.signInWithEmailAndPassword(email, password);
              log.info('Signed in with email and password through firebase.');
            } catch (error) {
              stytchEnrolGateRef.current = false;
              throw error;
            }
            // Lazily migrate this Firebase password into Stytch, then complete. Enrolment is gated
            // server-side by the stytch-password flag (evaluated with the authenticated identity), so
            // attempt it rather than pre-checking a client flag that can't see the user on /login.
            await enrolStytchPasswordAndComplete();
          }
          break;
        case LoginMethod.EMAIL_OTP: {
          await goToOtp();
          setMainButtonLoading(false);
          break;
        }
        case LoginMethod.NEW_USER:
          if (mode === 'login') {
            void Router.push(`/onboarding/customize-card?login_hint=${encodeURIComponent(email)}`);
          } else {
            if (!hasChosenPassword) {
              await proceedToDefaultMethod();
              setMainButtonLoading(false);
              return;
            }
            await signUserUpWithPassword();
          }
          break;
        case LoginMethod.GOOGLE:
          await signInOAuth({ email, providerId: 'google.com' });
          break;
        case LoginMethod.MICROSOFT:
          await signInOAuth({ email, providerId: 'microsoft.com' });
          break;
        case LoginMethod.APPLE:
          await signInOAuth({ email, providerId: 'apple.com' });
          break;
        case LoginMethod.OAUTH2PROXY:
          await signInOAuth({ email, providerId: 'oidc.oauth2proxy' });
          return;
      }
    } catch (err) {
      // Stytch password auth surfaces its own internal code with a non-user-facing message, and our
      // GraphQL rate limiter surfaces a "Rate limit exceeded" error. Map both here so neither internal
      // string reaches the user; Firebase AuthErrors (already user-friendly) fall through to the switch.
      if (err instanceof StytchPasswordError) {
        if (err.code === 'unauthorized_credentials') {
          setPasswordErrorMessage('Wrong password');
        } else {
          setErrorMessage(getStytchLoginErrorMessage(err.code));
        }
        setMainButtonLoading(false);
        return;
      }
      if (isRateLimitError(err)) {
        setErrorMessage('Your access has been temporarily restricted. Please try again later.');
        setMainButtonLoading(false);
        return;
      }
      const error = err as AuthError;
      switch (error?.code) {
        case 'auth/invalid-email':
          setLoginEmailFirebaseError(error.message);
          break;
        // Firebase returns invalid-credential (not wrong-password) once email-enumeration protection is on.
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
          setPasswordErrorMessage(error.message);
          break;
        case 'unauthorized_credentials':
          setPasswordErrorMessage('Wrong password');
          break;
        case 'auth/popup-blocked':
          log.warn('Sign in popup was blocked by the browser');
          setErrorMessage(
            'The sign-in popup was blocked by your browser. Please allow popups for this site and try again.',
          );
          break;
        case 'auth/popup-closed-by-user':
          log.info('Sign in popup was closed by user');
          break;
        case 'auth/provider-already-linked':
          log.warn('Sign in rejected: provider already linked under a different identity.', {
            method: method.methodId,
          });
          setIsProviderMismatch(true);
          setErrorMessage(getProviderMismatchMessage(method.methodId));
          break;
        case 'auth/network-request-failed':
        case 'auth/too-many-requests':
        case 'auth/user-not-found':
          log.warn(`Sign in attempt resulted in firebase error ${error.code}`);
          setErrorMessage(error.message);
          break;
        default:
          log.error('Unknown error when logging in', { error });
          setErrorMessage(error.message ?? 'An unknown error occurred');
          break;
      }
      setMainButtonLoading(false);
    }
  }

  async function handleReset() {
    const MESSAGE = 'Reset link action complete';
    try {
      setGeneratingEmailReset(true);
      await sendResetLink(email);
      logger.info(MESSAGE, { email });
      setSentResetEmail(true);
    } catch (err) {
      const error = err as AuthError;
      setErrorMessage(error.message);
      logger.error(MESSAGE, { email, error });
    } finally {
      setGeneratingEmailReset(false);
    }
  }

  async function handleLink(loginMethod: LoginMethodPayload) {
    const log = logger.child({ email });

    setPasswordErrorMessage('');

    if (loginMethod.ssoEnforced) {
      if (!loginMethod.defaultMethod) {
        // Sso is enforced but there is no SS0 methodId, we're stuck but this should never happen
        log.error('handleLink: SSO is enforced but no SSO methodId is provided');
        setErrorMessage('Login via SSO failed');
        setMainButtonLoading(false);
        return;
      }

      const oauthLoginProvider = mapLoginMethodToOAuthProvider(loginMethod.defaultMethod);
      await handleProvider(oauthLoginProvider)();
      return;
    }

    if (isPasswordError) {
      setMainButtonLoading(false);
      return;
    }

    // Set the gate before linking: the credential link wakes onIdTokenChanged, whose navigation would
    // otherwise unmount this form and cancel the Stytch enrolment below — the same race as
    // signUserUpWithPassword. This is the onboarding SignupStep path (mode 'link').
    stytchEnrolGateRef.current = true;
    try {
      setMainButtonLoading(true);
      await addEmailAndPasswordLoginToCurrentUser(email, password);
      log.info('Added email and password login to current user.');
    } catch (err) {
      stytchEnrolGateRef.current = false;
      const error = err as AuthError;
      switch (error.code) {
        case 'auth/network-request-failed':
          log.warn(`Sign up attempt resulted in firebase error with code ${error.code}`, { error });
          break;
        default:
          log.error('Unknown error adding email and password to current user.', { error });
          break;
      }
      setErrorMessage(error.message);
      setMainButtonLoading(false);
      return;
    }

    await enrolStytchPasswordAndComplete();
  }

  async function fetchUserAuthProfile(
    email: string,
    mode: 'link' | 'login' | 'reset' | 'hybrid',
    trigger: 'auto' | 'manual',
  ): Promise<LoginMethodPayload> {
    if (!email) {
      return { methodId: null, ssoEnforced: false, isExistingUser: false, type: 'otp_signup' };
    }
    setFetchingAuthProfile(trigger);
    const loginMethod = await pickLoginMethodForEmail(email);

    switch (true) {
      case loginMethod.methodId !== LoginMethod.NEW_USER &&
        mode === 'link' &&
        !loginMethod.ssoEnforced:
        setLoginEmailFirebaseError('This email has already been used to join Blinq.');
        break;
      case loginMethod.methodId === LoginMethod.OAUTH2PROXY && mode === 'reset':
        setErrorMessage(
          'Your account is linked to a SSO provider. You can not reset your password using this method.',
        );
        setAuthProfile(loginMethod);
        break;
      default:
        setAuthProfile(loginMethod);
        break;
    }

    setFetchingAuthProfile(undefined);
    return loginMethod;
  }

  async function handleSubmit(e: FormEvent) {
    try {
      e.preventDefault();
      // Always refetch after submission
      const authProfile_ = await fetchUserAuthProfile(email, mode, 'manual');

      if (beforeLoginHook) {
        setMainButtonLoading(true);
        const beforeLoginError = await beforeLoginHook(email);
        if (beforeLoginError) {
          setErrorMessage(beforeLoginError);
          setMainButtonLoading(false);
          return;
        }
      }

      // This is to cover for when someone has only signed up with an Email
      // OTP and the feature flag is off, meaning they have no password set.
      if (
        !hasChosenPassword &&
        authProfile_.isExistingUser &&
        authProfile_.enabledMethods?.length === 0
      ) {
        logger.warn('No enabled login methods for user. Catastrophe flow triggered.', { email });
        goToCatastropheFlow();
        analytics.track({
          name: 'otp_failed_viewed',
          properties: {
            type: authProfile_.type,
            request_id: requestId.current,
          },
        });
        return;
      }

      /**
       * Check if a work email is preferred during sign up
       */
      if (
        preferWorkEmail &&
        authProfile_.publicEmailAddress &&
        setEnteredPersonalEmail &&
        !favouredEmail
      ) {
        setEnteredPersonalEmail(true);
        return;
      }

      if (authProfile_.methodId === LoginMethod.NEW_USER && password !== '' && pwValidatorsPassed) {
        const pw = new IsPwned();

        try {
          // either returns true or throws an error
          await pw.check(password);
        } catch (e) {
          // do this to avoid type error
          if (!(e instanceof Error)) {
            return;
          }

          switch (e.name) {
            case 'BreachedError':
              // the following code will execute if password has been pwned
              analytics.track({ name: 'haveibeenpwned_pwnedpassword_entered' });

              break;
            default:
              // if some other error from API, this will execute
              analytics.track({ name: 'haveibeenpwned_api_failed' });
          }
        }
      }

      if (['login', 'hybrid', 'link'].includes(mode)) {
        analytics.track({
          name: 'continue_clicked',
          properties: {
            ref_page: authProfile_.isExistingUser ? 'dashboard_signin' : 'dashboard_signup',
            type: authProfile_.type,
            request_id: requestId.current,
            email,
            domain: email.split('@')[1],
          },
        });
      }

      setPasswordErrorMessage('');
      setSubmitted(true);

      // The provider rejected this account's identity, but the email code resolves to the same user.
      if (isProviderMismatch && authProfile_.isExistingUser) {
        setMainButtonLoading(true);
        await goToOtp();
        setMainButtonLoading(false);
        return;
      }

      switch (mode) {
        case 'reset':
          await handleReset();
          break;
        case 'login':
        case 'hybrid':
          await handleLogin(authProfile_);
          break;
        case 'link':
          // if the account exists, don't proceed to the password/otp screen so they see the error message
          // raised in fetchUserAuthProfile
          if (authProfile_.methodId !== LoginMethod.NEW_USER && !authProfile_.ssoEnforced) {
            return;
          }
          if (!hasChosenPassword && !hasChosenOtp) {
            setMainButtonLoading(true);
            await proceedToDefaultMethod();
            setMainButtonLoading(false);
            return;
          }
          await handleLink(authProfile_);
          break;
      }
    } catch (error) {
      logger.error('Unknown error occurred in handleSubmit', { email, error });
      setErrorMessage('An error occurred. Please try again');
      setMainButtonLoading(false);
    }
  }

  function handleProvider(provider: OAuthProvider): () => Promise<void> {
    return () => {
      switch (mode) {
        case 'link':
          return addSocialProviderLoginToCurrentUser(provider);
        case 'hybrid':
        case 'login':
        default:
          return usePopupAuth
            ? authService.signInWithPopup({ email, providerId: provider })
            : authService.signInWithRedirect({ email, providerId: provider });
      }
    };
  }

  function getActionText() {
    if (!email || authProfile.ssoEnforced) {
      return 'Continue';
    }

    if (isProviderMismatch && authProfile.isExistingUser) {
      return 'Continue with code';
    }

    switch (getLoginPreference() + mode) {
      case LoginMethod.PASSWORD + 'login':
      case LoginMethod.PASSWORD + 'hybrid':
        if (hasChosenPassword) {
          return authProfile.isExistingUser ? 'Sign in' : 'Sign up';
        }
        return 'Continue';
      case LoginMethod.NEW_USER + 'login':
        return 'Sign up';
      case LoginMethod.PASSWORD + 'link':
      case LoginMethod.NEW_USER + 'hybrid':
        if (hasChosenPassword) {
          return 'Sign up';
        }
        return 'Create account';
      default:
        return 'Continue';
    }
  }

  // Automatically find the login method for the email
  // biome-ignore lint/correctness/useExhaustiveDependencies: fetchUserAuthProfile/setAuthProfile/setResendCodeSeconds are unmemoized; depending on them retriggers the debounced lookup every render
  useEffect(() => {
    // clear otp method id and resend code timer when email changes
    setOtpMethodId(null);
    setResendCodeSeconds(null);

    if (!isEmail(email)) {
      setAuthProfile({
        methodId: null,
        ssoEnforced: false,
        publicEmailAddress: false,
        isExistingUser: false,
        type: 'otp_signup',
      });
      return;
    }

    const timeout = setTimeout(() => {
      void fetchUserAuthProfile(email, mode, 'auto');
    }, 300);

    return () => clearTimeout(timeout);
  }, [email, mode]);

  // Target the password input
  useEffect(() => {
    if (authProfile.methodId === LoginMethod.PASSWORD && passwordInputNode) {
      passwordInputNode.focus();
    }
  }, [authProfile, passwordInputNode]);

  // Run handlers on initial load
  // biome-ignore lint/correctness/useExhaustiveDependencies: onEmailChange is a parent prop that may be unmemoized; depending on it would re-resolve the redirect result every render
  useEffect(() => {
    (async () => {
      const isLinkMode = mode === 'link';
      const ssoRedirectMethod = ssoRedirectMethodRef.current;
      const expectsRedirectResult = isLinkMode || !!ssoRedirectMethod;

      if (expectsRedirectResult) {
        logger.info('Starting Firebase redirect result resolution.', {
          is_link_mode: isLinkMode,
          sso_redirect_method: ssoRedirectMethod,
        });
      }

      try {
        const redirectResult = await authService.getRedirectResult();
        const userFromRedirect = redirectResult?.user;
        const providerLinked = (userFromRedirect?.providerData.length || 0) >= 1;

        if (expectsRedirectResult || redirectResult) {
          logger.info('Firebase redirect result resolved.', {
            has_redirect_result: !!redirectResult,
            has_user_from_redirect: !!userFromRedirect,
            is_link_mode: isLinkMode,
            provider_ids:
              userFromRedirect?.providerData.map((provider) => provider.providerId) ?? [],
            provider_linked: providerLinked,
            sso_redirect_method: ssoRedirectMethod,
            user_id: userFromRedirect?.uid ?? null,
          });
        }

        if (providerLinked && isLinkMode && onLink) {
          return onLink();
        }
        if (userFromRedirect || isLinkMode) {
          // Prevent from setting loading to false
          return;
        }
        setLoading(false);
        setSubmitted(false);
      } catch (err) {
        const error = err as AuthError;
        logger.error('Firebase redirect result resolution failed.', {
          error,
          is_link_mode: isLinkMode,
          sso_redirect_method: ssoRedirectMethod,
        });
        setSubmitted(false);
        if (!isLinkMode && error.code === 'auth/provider-already-linked') {
          // The redirect reloaded the page; restore the email typed before it so the user can continue
          // with a code straight away.
          if (ssoRedirectEmailRef.current) {
            onEmailChange(ssoRedirectEmailRef.current);
          }
          setIsProviderMismatch(true);
          setErrorMessage(getProviderMismatchMessage(ssoRedirectMethod));
        } else {
          setErrorMessage(error.message);
        }
        setLoading(false);
      }
    })();
  }, [mode, onLink]);

  const handleEmailChange = (event: FormEvent<HTMLInputElement>) => {
    if (loginEmailFirebaseError) {
      setLoginEmailFirebaseError('');
    }
    const email = event.currentTarget.value;
    GoogleTagManagerUtils.setUserData({ email });
    GoogleTagManagerUtils.sendFormSubmitEvent();
    onEmailChange(email);
  };

  const handleSelectWorkEmail = () => {
    setFavouredEmail?.('work');
    // Reset form state
    onEmailChange('');
    onPasswordChange('');
  };

  const handleSelectPersonalEmail = () => {
    setFavouredEmail?.('personal');
  };

  if (stytchVerifyState) {
    return (
      <div className={styles['login-form']}>
        <p className={styles['email-otp-login-text']}>
          Last step — verify your email to secure your password. Enter the code we sent to{' '}
          <strong>{stytchVerifyState.email}</strong>.
        </p>
        <EmailVerify
          email={stytchVerifyState.email}
          initialEmailId={stytchVerifyState.emailId}
          onVerified={() => void onStytchEmailVerified()}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <span className={styles['loader']}>
        <Loader />
      </span>
    );
  }

  const showSwitchToWorkEmailPrompt = preferWorkEmail && enteredPersonalEmail && !favouredEmail;
  if (showSwitchToWorkEmailPrompt) {
    return (
      <SwitchToWorkEmailPrompt
        onSelectWorkEmail={handleSelectWorkEmail}
        onSelectPersonalEmail={handleSelectPersonalEmail}
        email={email}
      />
    );
  }

  return (
    <CallbackErrorBoundary
      onError={(error, errorInfo) => {
        logger.error('Uncaught error inside LoginForm.', { error, errorInfo });
      }}
    >
      <div className={styles['login-form']}>
        {!!errorMessage && (
          <div className={styles['error-message-container']}>
            <Banner25
              type="negative"
              size="large"
              message={errorMessage}
              onClose={() => setErrorMessage('')}
              dismissable
            />
          </div>
        )}
        {isCatastrophe && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <AnimatePresence>
              <Button
                size="large25"
                width="full"
                variant="solid25"
                onClick={() => {
                  void sendResetLink(email);
                  goToPassword();
                  onShowLogin();
                }}
              >
                <motion.span
                  key="catastrophic-error"
                  initial={{ opacity: 0 }}
                  animate={{
                    opacity: 1,
                    transition: { ease: 'easeOut', duration: 0.3 },
                  }}
                  exit={{
                    opacity: 0,
                    transition: { ease: 'easeIn', duration: 0.3 },
                  }}
                >
                  Continue with password instead
                </motion.span>
              </Button>
            </AnimatePresence>
          </motion.div>
        )}
        {!isCatastrophe && !resetPasswordMode && !hasChosenOtp && (
          <motion.form
            className={styles['email-login-container']}
            onSubmit={handleSubmit}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {!hasChosenPassword && (
              <div className={styles['email-input-container']}>
                <TextField
                  size="large25"
                  label="Email"
                  value={email}
                  onChange={handleEmailChange}
                  type="email"
                  autoComplete="email"
                  ref={emailLoginInputRef}
                  tone={
                    loginEmailFirebaseError || (isLoginEmailError && emailLoginInputBlurred)
                      ? 'critical25'
                      : 'neutral25'
                  }
                  message={
                    loginEmailFirebaseError || (isLoginEmailError && emailLoginInputBlurred)
                      ? loginEmailFirebaseError || loginEmailErrorMessage
                      : undefined
                  }
                  readOnly={lockEmail}
                  onClear={
                    enableClearEmailButton && !lockEmail
                      ? () => {
                          onEmailChange('');
                        }
                      : undefined
                  }
                />
              </div>
            )}
            {hasChosenPassword && (
              <div className={styles['password-login-container']}>
                <TextField
                  size="large25"
                  label="Password"
                  value={password}
                  onChange={(event) => {
                    onPasswordChange(event.currentTarget.value);
                  }}
                  type={isPasswordVisible ? 'text' : 'password'}
                  autoComplete="current-password"
                  ref={passwordInputRef}
                  disabled={authProfile.ssoEnforced}
                  adornment={
                    <button
                      type="button"
                      className={styles['password-visibility-toggle']}
                      aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
                      disabled={authProfile.ssoEnforced}
                      onClick={() => setIsPasswordVisible((visible) => !visible)}
                    >
                      {isPasswordVisible ? <IconEyeHide /> : <IconEyeShow />}
                    </button>
                  }
                  message={
                    submitted &&
                    // Suppress password validation while switching to the OTP flow: clicking
                    // "Continue with code" blurs an untouched password field, which would
                    // otherwise surface a spurious error during the switch.
                    !isContinuingWithCode &&
                    (passwordErrorMessage ||
                      (authProfile.methodId === LoginMethod.NEW_USER &&
                        newPasswordErrorMessage &&
                        passwordHasBlurred))
                      ? passwordErrorMessage || newPasswordErrorMessage
                      : undefined
                  }
                  tone={
                    submitted &&
                    !isContinuingWithCode &&
                    (passwordErrorMessage ||
                      (authProfile.methodId === LoginMethod.NEW_USER &&
                        newPasswordErrorMessage &&
                        passwordHasBlurred))
                      ? 'critical25'
                      : 'neutral25'
                  }
                />
              </div>
            )}

            <div className={styles['form-btns-container']}>
              <div className={styles['main-btn-container']}>
                <AnimatePresence>
                  <Button
                    size="large25"
                    width="full"
                    variant="solid25"
                    disabled={
                      !email ||
                      isLoginEmailError ||
                      !!loginEmailFirebaseError ||
                      isContinuingWithCode
                    }
                    loading={fetchingLoginMethods === 'manual' || mainButtonLoading}
                    type="submit"
                  >
                    <motion.span
                      key={getActionText()}
                      initial={{ opacity: 0 }}
                      animate={{
                        opacity: 1,
                        transition: { ease: 'easeOut', duration: 0.3 },
                      }}
                      exit={{
                        opacity: 0,
                        transition: { ease: 'easeIn', duration: 0.3 },
                      }}
                    >
                      {getActionText()}
                    </motion.span>
                  </Button>
                </AnimatePresence>
                {hasChosenPassword && (
                  <AnimatePresence>
                    {authProfile.enabledMethods?.includes(LoginMethod.EMAIL_OTP) && (
                      <Button
                        key="continue-with-code-button"
                        size="large25"
                        width="full"
                        variant="outline25"
                        type="button"
                        disabled={mainButtonLoading || isContinuingWithCode}
                        loading={isContinuingWithCode}
                        onClick={async () => {
                          setIsContinuingWithCode(true);
                          await goToOtp();
                          setIsContinuingWithCode(false);
                        }}
                      >
                        <motion.span
                          key="otp-continue-with-code-text"
                          initial={{ opacity: 0 }}
                          animate={{
                            opacity: 1,
                            transition: { ease: 'easeOut', duration: 0.3 },
                          }}
                          exit={{
                            opacity: 0,
                            transition: { ease: 'easeIn', duration: 0.3 },
                          }}
                        >
                          Continue with code
                        </motion.span>
                      </Button>
                    )}
                    {mode === 'hybrid' && (
                      <Button
                        key="go-back-button"
                        size="large25"
                        width="full"
                        variant="outline25"
                        type="button"
                        disabled={mainButtonLoading || isContinuingWithCode}
                        onClick={() => goBack()}
                      >
                        <motion.span
                          key="back-button-text"
                          initial={{ opacity: 0 }}
                          animate={{
                            opacity: 1,
                            transition: { ease: 'easeOut', duration: 0.3 },
                          }}
                          exit={{
                            opacity: 0,
                            transition: { ease: 'easeIn', duration: 0.3 },
                          }}
                        >
                          Back
                        </motion.span>
                      </Button>
                    )}
                    {mode === 'login' && (
                      <Button
                        key="forgot-password-button"
                        size="large25"
                        width="full"
                        variant="text25"
                        disabled={mainButtonLoading || isContinuingWithCode}
                        type="button"
                        onClick={() => {
                          setLoginState?.(LoginState.RESET_PASSWORD);
                          onShowReset();
                        }}
                      >
                        <motion.span
                          key="forgot-password-text"
                          initial={{ opacity: 0 }}
                          animate={{
                            opacity: 1,
                            transition: { ease: 'easeOut', duration: 0.3 },
                          }}
                          exit={{
                            opacity: 0,
                            transition: { ease: 'easeIn', duration: 0.3 },
                          }}
                        >
                          Forgot Password?
                        </motion.span>
                      </Button>
                    )}
                  </AnimatePresence>
                )}
              </div>
              {!hasChosenPassword && (
                <motion.div
                  key="sso-button-container"
                  className={styles['container']}
                  animate={{
                    opacity: authProfile.methodId === LoginMethod.OAUTH2PROXY ? 0.3 : 1,
                  }}
                >
                  <hr className={styles['hr']} />
                  <SSOLoginButton
                    name="Google"
                    logo="/img/google-logo.svg"
                    onClick={handleProvider('google.com')}
                    disabled={authProfile.methodId === LoginMethod.OAUTH2PROXY}
                  />
                  <SSOLoginButton
                    name="Microsoft"
                    logo="/img/microsoft-logo.svg"
                    onClick={handleProvider('microsoft.com')}
                    disabled={authProfile.methodId === LoginMethod.OAUTH2PROXY}
                  />
                  <SSOLoginButton
                    name="Apple"
                    logo="/img/apple-logo.svg"
                    onClick={handleProvider('apple.com')}
                    disabled={authProfile.methodId === LoginMethod.OAUTH2PROXY}
                  />
                </motion.div>
              )}
            </div>
          </motion.form>
        )}

        {!isCatastrophe && hasChosenOtp && (
          <motion.div
            className={styles['email-otp-login-container']}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <Toast ref={loginSentToastRef} size="mini" type="black" text="Sign in code sent" />
            <p className={styles['email-otp-login-text']}>
              Once you enter the code we sent you to <strong>{email}</strong>, you&#39;ll be all
              signed in.
            </p>
            <EmailOtpPinInput
              error={otpError}
              ref={pinInputRef}
              value={otpCode}
              onChange={(code) => {
                setOtpError('');
                setOtpCode(code);
              }}
            />
            <div className={styles['email-otp-resend-code-container']}>
              <p className={styles['email-otp-resend-code-text']}>
                Didn't receive a code?
                <button
                  className={styles['email-otp-resend-code-button']}
                  disabled={mainButtonLoading || isResendingCode || !!resendCodeSeconds}
                  type="button"
                  onClick={async () => {
                    setIsResendingCode(true);
                    const type = authProfile.isExistingUser ? 'otp_login' : 'otp_signup';
                    analytics.track({
                      name: 'otp_resend_clicked',
                      properties: {
                        ref_page: refPage,
                        type,
                        request_id: requestId.current,
                      },
                    });
                    const success = await sendOtpCode();
                    if (success) {
                      setOtpCode('');
                      loginSentToastRef.current?.show(3000);
                      pinInputRef.current?.focus();
                    }
                    setIsResendingCode(false);
                  }}
                >
                  {resendCodeSeconds
                    ? `Resend code in 0:${resendCodeSeconds / 10 < 1 ? '0' : ''}${resendCodeSeconds}`
                    : 'Resend code'}
                </button>
              </p>
            </div>

            <div className={styles['form-btns-container']}>
              <div className={styles['main-btn-container']}>
                <Button
                  size="large25"
                  width="full"
                  variant="solid25"
                  disabled={otpCode.length < 6 || mainButtonLoading || isResendingCode}
                  loading={mainButtonLoading}
                  onClick={handleOtpLogin}
                  type="button"
                >
                  Continue
                </Button>
                {authProfile.enabledMethods?.includes(LoginMethod.PASSWORD) && (
                  <Button
                    size="large25"
                    width="full"
                    variant="outline25"
                    disabled={mainButtonLoading}
                    type="button"
                    onClick={() => {
                      goToPassword();
                      setOtpCode('');
                      setOtpError('');
                      analytics.track({
                        name: 'otp_password_clicked',
                        properties: {
                          ref_page: refPage,
                          type: authProfile.type,
                          request_id: requestId.current,
                        },
                      });
                    }}
                  >
                    Continue with password
                  </Button>
                )}
                {mode === 'hybrid' && (
                  <Button
                    key="otp-go-back-button"
                    size="large25"
                    width="full"
                    variant="outline25"
                    type="button"
                    disabled={mainButtonLoading}
                    onClick={() => goBack()}
                  >
                    <motion.span
                      key="otp-back-button-text"
                      initial={{ opacity: 0 }}
                      animate={{
                        opacity: 1,
                        transition: { ease: 'easeOut', duration: 0.3 },
                      }}
                      exit={{
                        opacity: 0,
                        transition: { ease: 'easeIn', duration: 0.3 },
                      }}
                    >
                      Back
                    </motion.span>
                  </Button>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {!isCatastrophe && resetPasswordMode && (
          <>
            {!sentResetEmail && (
              <motion.form
                className={styles['email-reset-password-container']}
                onSubmit={handleSubmit}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <TextField
                  size="large25"
                  label="Email"
                  value={email}
                  onChange={handleEmailChange}
                  type="email"
                  autoComplete="email"
                  ref={resetEmailInputRef}
                  tone={isResetEmailError && resetEmailInputBlurred ? 'critical25' : 'neutral25'}
                  message={
                    isResetEmailError && resetEmailInputBlurred ? resetEmailErrorMessage : undefined
                  }
                  readOnly={lockEmail}
                  onClear={
                    enableClearEmailButton && !lockEmail
                      ? () => {
                          onEmailChange('');
                        }
                      : undefined
                  }
                />

                <div className={styles['form-btns-container']}>
                  <div className={styles['main-btn-container']}>
                    <Button
                      size="large25"
                      variant="solid25"
                      width="full"
                      disabled={!email || authProfile.methodId === LoginMethod.OAUTH2PROXY}
                      loading={generatingEmailReset}
                      type="button"
                      onClick={handleReset}
                    >
                      Send reset link
                    </Button>
                    <Button
                      size="large25"
                      variant="outline25"
                      width="full"
                      type="button"
                      disabled={!email || authProfile.methodId === LoginMethod.OAUTH2PROXY}
                      onClick={goBack}
                    >
                      Return to sign in
                    </Button>
                  </div>
                </div>
              </motion.form>
            )}

            {sentResetEmail && (
              <p className={styles['sent-reset-email-message']}>
                Check your inbox for the link to reset your password
              </p>
            )}
          </>
        )}
      </div>
    </CallbackErrorBoundary>
  );
};

interface EmailOtpInputProps {
  error: string;
  value: string;
  onChange: (code: string) => void;
}

export const EmailOtpPinInput = ({
  ref,
  error,
  value,
  onChange,
}: EmailOtpInputProps & {
  ref?: React.Ref<HTMLInputElement>;
}) => {
  return (
    <div className={styles['email-otp-pin-input-container']}>
      <PinInput
        classNames={{
          input: styles['email-otp-pin-input'],
        }}
        onPaste={(e) => {
          e.preventDefault();
          const value = e.clipboardData.getData('text');
          onChange(value);
        }}
        error={!!error}
        ref={ref}
        oneTimeCode
        autoFocus
        gap={8}
        size="lg"
        value={value}
        length={6}
        onChange={onChange}
        placeholder=""
        type="number"
      />
      {error && (
        <p className={styles['email-otp-pin-input-error']}>
          <IconCloseCircle />
          {error}
        </p>
      )}
    </div>
  );
};
